import { CUSTOM_FIELD_HEADERS, VALID_HEADERS } from '@constants';
import { ParseResult } from 'papaparse';
import { CsvProduct } from '@pages/Products/ProductCsvReader/ProductCsvReader';
import { CustomField, Product, ProductCategory, ProductImageFull } from '@models';

type CustomFields = { productId: number; customFieldId: number };

type Props = {
  results: ParseResult<string[]>;
  addInvalidHeader: (header: string) => void;
  addValidationError: (error: string) => void;
  clearState: () => void;
  products: Product[];
  setCustomFieldsToDelete: (customFields: CustomFields[]) => void;
  setProductsToCreate: (prods: Partial<Product>[]) => void;
  setProductsToUpdate: (prods: Partial<Product>[]) => void;
  imageURL?: string;
  categories?: ProductCategory[];
};

type MapProductsBySKUProps = {
  csvProducts: CsvProduct[];
  addValidationError: (error: string) => void;
  products: Product[];
  categories?: ProductCategory[];
  imageURL?: string;
  setCustomFieldsToDelete: (customFields: CustomFields[]) => void;
  setProductsToCreate: (prods: Partial<Product>[]) => void;
  setProductsToUpdate: (prods: Partial<Product>[]) => void;
};

export const convertSEOURL = (url: string) =>
  url
    .normalize('NFD') // Change diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove illegal characters
    .replace(/\s+/g, '_') // Change whitespace to dashes
    .toLowerCase() // Change to lowercase
    .replace(/&/g, '_and_') // Replace ampersand
    .replace(/\d/g, ''); // Remove anything that is not a letter, number or hyphen

export const convertCsvToProductArray = (csv: ParseResult<string[]>, addInvalidHeader: (header: string) => void): CsvProduct[] => {
  const headers: string[] = csv.data[0];

  //Validate Headers
  headers.forEach((header) => {
    const headerIsValid = VALID_HEADERS.includes(header);

    if (!headerIsValid) {
      addInvalidHeader(header);
    }
  });

  const rows = csv.data.slice(1);

  return rows.map((row) => {
    return headers.reduce(
      (product: CsvProduct, header: string, i: number) => ({
        ...product,
        [header]: row[i],
      }),
      {},
    );
  });
};

export const mapProductsBySku = ({
  csvProducts,
  addValidationError,
  products,
  categories,
  imageURL,
  setCustomFieldsToDelete,
  setProductsToCreate,
  setProductsToUpdate,
}: MapProductsBySKUProps): void => {
  const productsToCreate: Partial<Product>[] = [];
  const productsToUpdate: Partial<Product>[] = [];
  const customFieldsToDelete: { productId: number; customFieldId: number }[] = [];

  if (csvProducts[0].sku === undefined) {
    addValidationError('No SKU column found in CSV file');
    return;
  }

  csvProducts
    .filter((item) => item.sku) // filter items
    .forEach((csvProduct) => {
      const product: Partial<Product> = csvProduct;
      const existingProduct = products.find((p) => p.sku === csvProduct.sku);

      const getCustomFieldByName = (fieldName: string) => existingProduct?.custom_fields?.find((field) => field.name === fieldName);

      const validateAndConvertToBoolean = (field: string) => {
        if (csvProduct[field].trim() === '') csvProduct[field] = 'false';

        if (csvProduct[field].toLowerCase() !== 'true' && csvProduct[field].toLowerCase() !== 'false') {
          addValidationError(`Value for ${field} must be either "true" or "false".`);
        }

        return csvProduct[field].toLowerCase() === 'true';
      };

      const validateAndConvertToNumber = (field: string) => {
        const valueAsNumber = Number(csvProduct[field]);

        if (isNaN(valueAsNumber)) {
          addValidationError(`Value for ${field} must be a number.`);
        }

        return valueAsNumber;
      };

      // Check whether the mandatory fields are present when creating a new product.
      if (!existingProduct && !csvProduct.name) {
        addValidationError(`The "name" field is missing for ${csvProduct.sku}`);
      }

      if (!existingProduct && !csvProduct.price) {
        addValidationError(`The "price" field is missing for ${csvProduct.sku}`);
      }

      if (!existingProduct && !csvProduct.weight) {
        addValidationError(`The "weight" field is missing for ${csvProduct.sku}`);
      }

      // Need to cast all values that aren't supposed to be strings.
      if (csvProduct.cost_price) {
        product.cost_price = validateAndConvertToNumber('cost_price');
      }

      if (csvProduct.is_visible) {
        product.is_visible = validateAndConvertToBoolean('is_visible');
      }

      if (csvProduct.price) {
        product.price = validateAndConvertToNumber('price');
      }

      if (csvProduct.weight) {
        product.weight = validateAndConvertToNumber('weight');
      }

      const customFields = Object.entries(csvProduct).filter(([key]) => CUSTOM_FIELD_HEADERS.includes(key));

      // Custom fields from CSV need to be converted into proper custom fields for the updated product.
      for (const [key, value] of customFields) {
        const existingField = getCustomFieldByName(key) as CustomField;

        if (!product.custom_fields) {
          product.custom_fields = [];
        }

        const validatedValue =
          key === 'display_menu_tile'
            ? validateAndConvertToBoolean('display_menu_tile')
            : key === 'standard_drink_count'
            ? validateAndConvertToNumber('standard_drink_count')
            : value;

        if (existingProduct && existingField) {
          if (value === '') {
            // Existing field has been changed to empty and should be removed.
            customFieldsToDelete.push({ productId: existingProduct.id, customFieldId: existingField.id });
          } else {
            // Existing field should continue to exist.
            product.custom_fields?.push({ ...existingField, value: `${validatedValue}` });
          }
        } else if (value !== '') {
          // A new custom field should be added to the product.
          product.custom_fields?.push({ name: key, value: `${validatedValue}` } as CustomField);
        }
      }

      // If there were no custom field modifications, remove the empty array from the product.
      if (product.custom_fields?.length === 0) {
        delete product.custom_fields;
      }

      if (csvProduct.custom_url) {
        // The custom_url in the CSV is a string,but it needs to be an object on the product.
        product.custom_url = {
          is_customized:
            existingProduct?.custom_url?.url !== csvProduct.custom_url ? true : existingProduct?.custom_url?.is_customized || false,
          url: csvProduct.custom_url,
        };
      } else if (csvProduct.custom_url === '') {
        product.custom_url = {
          is_customized: false,
          url: '',
        };
      } // ignore if its undefined

      // Update the custom_url on the product if it has changed.
      if ((csvProduct.custom_url?.url === '' || csvProduct.custom_url?.url.indexOf('_')) && csvProduct.name) {
        const slug = csvProduct.name.replace(/[^\w\s]/gi, '');

        csvProduct.custom_url.url = `/${slug.toLowerCase().replace(/\s/g, '-')}/`;
        csvProduct.custom_url.url = `/${slug.toLowerCase().replaceAll(/&/g, 'and')}/`;
        csvProduct.custom_url.is_customized = true;
      }

      if (csvProduct.categories || csvProduct.zones) {
        const categoryNames = csvProduct.categories?.toLowerCase()?.split(',') || [];
        const zoneNames = csvProduct.zones?.toLowerCase()?.split(',') || [];

        const allNames = [...categoryNames, ...zoneNames];

        const categoryIds = categories?.reduce(
          (ids, category) => (allNames.includes(category.name.toLowerCase()) ? [...ids, category.id] : ids),
          [] as number[],
        );

        if (categoryIds) {
          product.categories = categoryIds;
        }
      }

      /**
       * Get the image path from the dialog screen
       * Construct the image URL and pass it in for upload
       */
      if (csvProduct.product_image_file && csvProduct.product_image_file !== '') {
        const allImages = csvProduct.product_image_file.split(',');
        const allDesc = csvProduct.product_image_description?.split(',');

        csvProduct.images = allImages?.map((img: ProductImageFull, index: number) => ({
          is_thumbnail: !index,
          image_url: `${imageURL}/${csvProduct.sku}/${String(img).trim()}`,
          description: allDesc && allDesc[index] ? String(allDesc[index]).trim() : '',
        }));
      }

      // delete the prop if doesnt exist
      delete csvProduct.product_image_file;

      if (existingProduct) {
        productsToUpdate.push({ id: existingProduct?.id, ...product });
      } else {
        // Adding a default type here because all our products are physical.
        productsToCreate.push({ ...product, type: 'physical' });
      }
    });

  if (customFieldsToDelete) {
    setCustomFieldsToDelete(customFieldsToDelete);
  }

  setProductsToCreate(productsToCreate);
  setProductsToUpdate(productsToUpdate);
};

export const handleOnFileLoad = (props: Props) => {
  const {
    results,
    addInvalidHeader,
    addValidationError,
    clearState,
    products,
    setCustomFieldsToDelete,
    setProductsToCreate,
    setProductsToUpdate,
    imageURL,
    categories,
  } = props;

  clearState();

  const csvProducts = convertCsvToProductArray(results, addInvalidHeader);

  mapProductsBySku({
    csvProducts,
    addValidationError,
    products,
    categories,
    imageURL,
    setCustomFieldsToDelete,
    setProductsToCreate,
    setProductsToUpdate,
  });
};
